import { t } from 'i18next'
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import AddToBagButton from '../AddToBagButton'
import { LinkedItemSelection } from '../LinkedItemSelection'
import { VariantSelection } from '../VariantSelection'
import { ProductValues } from '../types'
import { useCallback, useEffect, useMemo } from 'react'
import { LiquidProductType } from '@/features/product/types/LiquidProduct'
import {
  useDefaultValues,
  useGiftCard,
  useInStock,
  useVariantUpdate,
} from '../hooks'
import classNames from 'classnames'
import { twMerge } from 'tailwind-merge'
import { OutOfStockForm } from '../OutOfStockForm'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { zodResolver } from '@hookform/resolvers/zod'
import zod from '@lounge-fe/validation/src/zod'
import { ZodTypeAny } from 'zod'
import { useProductPageStore } from '../hooks/useProductPageStore'
import { RestError } from '@lounge-fe/network'
import { base64 } from '@/utils/base64'
import { useSearchParams } from '@/hooks/useSearchParams'
import { useAddCartItems } from '@/features/cart/hooks'
import { GiftCardRecipient } from '../GiftCardRecipient'
import { ProductFormInnerProps, ProductFormProps } from './types'
import { useToasts, Text, addImageSizeParametersToUrl } from '@lounge-fe/ui-kit'
import { useProductTracking } from '../hooks/useProductTracking'
import { AddToWishlist } from '@/features/product/components/AddToWishlist'

const ProductFormInner = ({
  product,
  hasOnlyDefaultVariant,
  options,
  variants,
  links,
  sizeGuides,
  sizeGuidesV2,
  primaryAction,
  secondaryAction,
  hideOutOfStockForm,
  backInStock,
  giftcard: isGiftCard,
  onSuccess,
  onVariantSelection,
  showWishlistButton,
  className,
}: ProductFormInnerProps) => {
  const { handleSubmit } = useFormContext<ProductValues>()
  const { getFlag } = useFeatureFlags()
  const { toast } = useToasts()
  const { isLowStock, isInStock } = useInStock()
  const addItems = useAddCartItems()
  const tracking = useProductTracking(product)

  const giftcard = useGiftCard({
    isGiftCard: !!isGiftCard,
    options,
    enabled: getFlag('product_gift_card_recipient'),
  })

  const [variantValue, linkValues, selectedOptions] = useWatch<
    ProductValues,
    ['variant', 'links', 'options']
  >({ name: ['variant', 'links', 'options'] })

  const memoizedVariants = useMemo(() => variants ?? [], [variants])

  useVariantUpdate('options', 'variant', memoizedVariants)

  const isSelectOption = (optionName: string) => {
    if (getFlag('product_choice_chips')) return false

    return ['band_size', 'cup_size'].includes(
      optionName.toLocaleLowerCase().replace(/\s/g, '_'),
    )
  }

  useEffect(() => {
    if (variantValue && onVariantSelection) {
      onVariantSelection(variantValue)
    }
  }, [variantValue])

  const sellWhilstOutOfStock = useMemo(
    () =>
      variantValue
        ? variantValue?.inventory_policy === 'continue' &&
          linkValues.every(
            (option) => option.variant?.inventory_policy === 'continue',
          )
        : false,
    [variantValue],
  )

  const onSubmit = useCallback<SubmitHandler<ProductValues>>(
    async ({ variant, links }) => {
      if (isInStock || sellWhilstOutOfStock) {
        if (!variant) return

        const giftCardVariant = variants?.find(({ id }) => variant.id === id)

        const variantIds = [
          variant.id,
          ...links.map((link) => link.variant!.id),
        ]

        try {
          await addItems.mutateAsync(
            variantIds.map((id) => ({
              id,
              quantity: 1,
              properties: {
                _setID: base64.encode(variantIds),
                _giftcard_image:
                  isGiftCard && giftCardVariant?.image?.src
                    ? addImageSizeParametersToUrl({
                        src: giftCardVariant?.image?.src,
                        width: 800,
                        height: 400,
                        crop: 'center',
                      })
                    : undefined,
              },
            })),
            {
              onSuccess: () => {
                tracking.onAddToCart()
                if (onSuccess) {
                  onSuccess()
                }
              },
            },
          )
        } catch (error) {
          if (error instanceof RestError) {
            toast({
              severity: 'critical',
              title: t('general.error'),
              message: error.data.description,
            })
          }
        }
      }
    },
    [isInStock, sellWhilstOutOfStock],
  )

  const selectedVariantIdAndSkus = useMemo(() => {
    return [
      [variantValue?.id, variantValue?.sku],
      ...linkValues.map((link) => [link.variant?.id, link.variant?.sku]),
    ].filter((values) => values.every(Boolean)) as [number, string][]
  }, [variantValue, linkValues])

  const isProductInStock = variants?.some(
    (variant) => variant.in_stock || variant.inventory_policy === 'continue',
  )

  const hasVariantSelection =
    (Boolean(variantValue) || selectedOptions.every(Boolean)) &&
    linkValues.every((link) => Boolean(link.variant))

  const showOutOfStockForm =
    !isProductInStock ||
    (!isInStock && !sellWhilstOutOfStock && hasVariantSelection)

  return (
    <>
      <form
        data-testid="product-form"
        className={twMerge('flex flex-col gap-4', className)}
        onSubmit={handleSubmit(onSubmit)}
      >
        {!hasOnlyDefaultVariant && (
          <VariantSelection
            options={options}
            variants={variants ?? []}
            getIsSelectOption={isSelectOption}
            isDisabledSelectable={!hideOutOfStockForm || options.length > 1}
            sizeGuides={sizeGuides}
            sizeGuidesV2={sizeGuidesV2}
            sizeGuidePrimaryAction={primaryAction ?? null}
            sizeGuideSecondaryAction={secondaryAction ?? null}
          />
        )}
        {links.map((link, idx) => (
          <LinkedItemSelection
            key={idx}
            title={link.title}
            name={`links[${idx}]`}
            linkedItems={link.items}
            isDisabledSelectable={!hideOutOfStockForm}
          />
        ))}
        {(!showOutOfStockForm ||
          (showOutOfStockForm && hideOutOfStockForm)) && (
          <div>
            {isLowStock && (
              <Text
                variant="title-six"
                className="text-critical flex items-center gap-x-3 text-sm before:content-empty before:rounded-circle before:w-[6px] before:h-[6px] before:bg-[currentColor] mb-4"
              >
                {t('product.low_stock')}
              </Text>
            )}
            <div className="flex flex-row gap-4">
              <AddToBagButton
                type="submit"
                productId={product.id}
                variantId={variantValue?.id}
                disabled={showOutOfStockForm}
              >
                {t('product.add_to_bag')}
              </AddToBagButton>
              {showWishlistButton && (
                <AddToWishlist
                  productId={product.id}
                  className="h-10 w-12 border-[#D1D1D1] aspect-[unset] flex-none [&>svg]:w-4 [&>svg]:h-4"
                />
              )}
            </div>
          </div>
        )}
      </form>

      {showOutOfStockForm && (
        <div className="mb-8">
          <Text
            variant="title-six"
            className={classNames(
              'text-critical flex items-center gap-x-3 text-sm',
              'before:content-empty before:rounded-circle before:w-[6px] before:h-[6px] before:bg-[currentColor]',
            )}
            style={
              backInStock?.accent_color
                ? { color: backInStock.accent_color }
                : undefined
            }
          >
            {backInStock?.title ?? t('product.out_of_stock')}
          </Text>
          {!hideOutOfStockForm && (
            <OutOfStockForm
              selectedVariantIdAndSkus={selectedVariantIdAndSkus}
              title={backInStock?.subtitle}
              body={backInStock?.body}
              successMessage={{
                title: backInStock?.success_message?.title,
                message: backInStock?.success_message?.message,
              }}
              event={backInStock?.event}
            />
          )}
        </div>
      )}

      {giftcard && giftcard.denominations && (
        <div className="mb-8">
          <GiftCardRecipient
            selectedVariant={variantValue}
            fallbackImage={variantValue?.image}
            title={product.title}
            variants={variants ?? []}
            options={giftcard.denominations}
          />
        </div>
      )}
    </>
  )
}

const ProductForm = (props: ProductFormProps) => {
  const {
    options,
    variants,
    linked_bottoms,
    product,
    className,
    showWishlistButton,
  } = props

  const productTypeTitleMap: Partial<Record<LiquidProductType, string | null>> =
    {
      'Intimates Set Bottoms': t('product.bottoms_size'),
      'Intimates Set Skirt': t('product.skirt_size'),
    }

  /**
   *  TODO: AND/OR logic for linked_bottoms
   *  Currently, the linked_bottoms are being filtered by the AND logic.
   *  We may need to implement the OR logic for certain products.
   */

  // const links = useMemo(
  //   () =>
  //     [
  //       {
  //         title: t('product.choose_your_bottoms'),
  //         items: linked_bottoms,
  //       },
  //     ].filter((link) => link.items.length > 0),
  //   [linked_bottoms]
  // )

  const links = useMemo(
    () => [
      ...(
        linked_bottoms?.map((linked_bottom) => ({
          title:
            linked_bottoms.length > 1
              ? (productTypeTitleMap?.[linked_bottom.type] ??
                linked_bottom.title)
              : t('product.choose_your_bottoms'),
          items: [linked_bottom],
        })) ?? []
      ).filter((link) => link.items.length > 0),
    ],
    [linked_bottoms],
  )

  const defaultValues = useDefaultValues(
    options,
    links,
    variants,
    product.selected_variant,
  )

  const optionSchemas = zod.tuple(
    options.map((option) =>
      zod.enum(option.values as [string, ...string[]], {
        required_error: t('product.select_an_option'),
      }),
    ) as unknown as [ZodTypeAny, ...ZodTypeAny[]],
  )

  const linksSchema = zod.tuple([
    ...links.map((link) =>
      zod.object({
        id: zod.string(),
        name: zod.string(),
        options: zod.array(
          zod.enum(
            link.items.flatMap((item) =>
              item.options.flatMap((option) => option.values),
            ) as [string, ...string[]],
            {
              required_error: t('product.select_an_option'),
            },
          ),
        ),
        variant: zod.object({
          id: zod.number(),
        }),
      }),
    ),
  ] as unknown as [ZodTypeAny, ...ZodTypeAny[]])

  const schema = zod.object({
    options: optionSchemas,
    variant: zod.object({
      id: zod.number(),
    }),
    links: linksSchema,
  })

  const methods = useForm<ProductValues>({
    defaultValues,
    resolver: zodResolver(schema),
  })
  return (
    <FormProvider {...methods}>
      <ProductFormInner
        {...props}
        links={links}
        className={className}
        showWishlistButton={showWishlistButton}
      />
    </FormProvider>
  )
}

export { ProductForm }
